import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Eye, Loader2, Save, Info, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';
import { requestTypeSettingsApi } from '../../lib/api';
import {
  InfoBoxCustomFields, DetailsCustomFields, CustomFieldsSections, formatFieldLabel,
} from '../overview/CustomFieldsDisplay';

const TYPE_BADGES = {
  text: 'Text', textarea: 'Text area', select: 'Dropdown', multiselect: 'Multi-select',
  date: 'Date', checkbox: 'Checkbox', file_upload: 'File upload', file_download: 'Template file',
};

const sampleValue = (def) => {
  switch (def?.type) {
    case 'file_upload': return { fileName: `${(def.label || 'document').split(' ')[0].toLowerCase()}.pdf` };
    case 'file_download': return { downloaded: true };
    case 'checkbox': return true;
    case 'date': return 'Jun 12, 2026';
    case 'select': return 'Option A';
    case 'multiselect': return 'Option A, Option B';
    case 'textarea': return 'Sample multi-line text';
    default: return 'Sample text';
  }
};

// Mock of the real Overview tab so admins see exactly where fields land
function OverviewPreview({ layoutDraft, fieldDefs, full = false }) {
  const values = {};
  fieldDefs.forEach((d) => { values[d.id] = sampleValue(d); });
  const layout = { ...layoutDraft, field_defs: fieldDefs };
  return (
    <div className={`grid grid-cols-1 ${full ? 'md:grid-cols-3' : ''} gap-4 ${full ? '' : 'text-sm'}`} data-testid="overview-preview">
      <Card className={full ? 'md:col-span-2' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className={full ? '' : 'text-base'}>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-slate-500">Description</Label>
            <p className="mt-1 text-slate-400 italic">Sample request description…</p>
          </div>
          <DetailsCustomFields layout={layout} values={values} testidPrefix="preview" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className={full ? '' : 'text-base'}>Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-slate-500">Company</p>
            <p className="font-medium">Acme Pharma</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Specialties</p>
            <Badge variant="outline" className="text-xs mt-1">Oncology (ONC)</Badge>
          </div>
          <InfoBoxCustomFields layout={layout} values={values} testidPrefix="preview" />
          <div>
            <p className="text-sm text-slate-500">Assigned Team</p>
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge className="bg-emerald-100 text-emerald-800 text-xs">Coordinator: Sara</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
      <CustomFieldsSections layout={layout} values={values} testidPrefix="preview" />
    </div>
  );
}

export function OverviewLayoutEditor({ requestType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [showFullPreview, setShowFullPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestTypeSettingsApi.getOverviewLayout(requestType);
      const { sections: secs, fields: flds, field_defs: defs } = res.data;
      setSections(secs);
      setFieldDefs(defs);
      // Build the full editable universe: configured entries first, new fields appended
      const configured = new Set(flds.map((f) => f.field_id));
      const universe = [
        ...flds.filter((f) => defs.some((d) => d.id === f.field_id)),
        ...defs.filter((d) => !configured.has(d.id)).map((d) => ({
          field_id: d.id, visible: true, placement: 'section', section_id: secs[0]?.id || 'default', display: 'value',
        })),
      ];
      setFields(universe);
    } catch {
      toast.error('Failed to load overview layout');
    } finally {
      setLoading(false);
    }
  }, [requestType]);

  useEffect(() => { load(); }, [load]);

  const defOf = (id) => fieldDefs.find((d) => d.id === id);
  const groupOf = useCallback(
    (f) => {
      if (f.placement === 'info_box') return 'info_box';
      if (f.placement === 'details') return 'details';
      return sections.some((s) => s.id === f.section_id) ? f.section_id : sections[0]?.id;
    },
    [sections]
  );

  const patchField = (fieldId, patch) =>
    setFields((prev) => prev.map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f)));

  const moveField = (fieldId, dir) => {
    setFields((prev) => {
      const arr = [...prev];
      const idx = arr.findIndex((f) => f.field_id === fieldId);
      if (idx === -1) return prev;
      const g = groupOf(arr[idx]);
      const step = dir === 'up' ? -1 : 1;
      let j = idx + step;
      while (j >= 0 && j < arr.length && groupOf(arr[j]) !== g) j += step;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const addSection = () => {
    const id = `sec_${Date.now()}`;
    setSections((prev) => [...prev, { id, title: 'New Section' }]);
  };
  const renameSection = (id, title) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  const moveSection = (id, dir) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      const j = idx + (dir === 'up' ? -1 : 1);
      if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const deleteSection = (id) => {
    if (sections.length <= 1) { toast.error('At least one section is required'); return; }
    const fallback = sections.find((s) => s.id !== id)?.id;
    setSections((prev) => prev.filter((s) => s.id !== id));
    setFields((prev) => prev.map((f) => (f.section_id === id ? { ...f, section_id: fallback } : f)));
  };

  const handleSave = async () => {
    if (sections.some((s) => !s.title.trim())) { toast.error('Section titles cannot be empty'); return; }
    setSaving(true);
    try {
      await requestTypeSettingsApi.updateOverviewLayout(requestType, {
        sections,
        fields: fields.map((f) => ({
          ...f,
          section_id: f.placement === 'info_box' || f.placement === 'details' ? null : groupOf(f),
        })),
      });
      toast.success('Overview layout saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save overview layout');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  if (fieldDefs.length === 0) {
    return (
      <div className="text-slate-500 text-sm py-4 flex items-center gap-2" data-testid="overview-layout-empty">
        <Info className="w-4 h-4" />
        No custom fields configured for {requestType} yet. Add fields in “Edit Request Form” first — then arrange how they appear on the Overview tab here.
      </div>
    );
  }

  const layoutDraft = { sections, fields };

  const renderFieldRow = (f) => {
    const def = defOf(f.field_id);
    const isFile = def?.type === 'file_upload';
    return (
      <div
        key={f.field_id}
        className={`flex items-center gap-2 p-2 rounded-md border border-slate-200 bg-white ${f.visible ? '' : 'opacity-50'}`}
        data-testid={`ovl-field-row-${f.field_id}`}
      >
        <div className="flex flex-col">
          <Button variant="ghost" size="sm" className="h-4 w-5 p-0" onClick={() => moveField(f.field_id, 'up')} data-testid={`ovl-field-up-${f.field_id}`}>
            <ChevronUp className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-4 w-5 p-0" onClick={() => moveField(f.field_id, 'down')} data-testid={`ovl-field-down-${f.field_id}`}>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">
            {def?.label || formatFieldLabel(f.field_id)}
            {def?.builtin && (
              <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 align-middle text-slate-500">Built-in</Badge>
            )}
          </p>
          <p className="text-[10px] text-slate-400">{TYPE_BADGES[def?.type] || def?.type}</p>
        </div>
        {isFile && (
          <Select value={f.display} onValueChange={(v) => patchField(f.field_id, { display: v })}>
            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`ovl-field-display-${f.field_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="value">File name</SelectItem>
              <SelectItem value="check">Checkmark only</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select
          value={f.placement === 'info_box' ? 'info_box' : f.placement === 'details' ? 'details' : groupOf(f)}
          onValueChange={(v) =>
            patchField(
              f.field_id,
              v === 'info_box'
                ? { placement: 'info_box', section_id: null }
                : v === 'details'
                  ? { placement: 'details', section_id: null }
                  : { placement: 'section', section_id: v }
            )
          }
        >
          <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`ovl-field-placement-${f.field_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="details">Details card</SelectItem>
            <SelectItem value="info_box">Information box</SelectItem>
            {sections.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Switch
          checked={f.visible}
          onCheckedChange={(v) => patchField(f.field_id, { visible: v })}
          data-testid={`ovl-field-visible-${f.field_id}`}
        />
      </div>
    );
  };

  const infoBoxFields = fields.filter((f) => f.placement === 'info_box');
  const detailsFields = fields.filter((f) => f.placement === 'details');

  return (
    <div className="space-y-4" data-testid={`overview-layout-editor-${requestType}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">
          Arrange how the custom form fields appear on the request’s <strong>Overview tab</strong> — group them into
          named sections, move key fields into the Information box, or show uploads as a checkmark only.
          {requestType === 'FT' && ' Applies to fields from both Path A and Path B.'}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowFullPreview(true)} data-testid="ovl-full-preview-btn">
            <Eye className="w-4 h-4 mr-1" /> Full Preview
          </Button>
          <Button type="button" size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={handleSave} disabled={saving} data-testid="ovl-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Layout
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* ---- Editor ---- */}
        <div className="space-y-4">
          {/* Details card group */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="ovl-group-details">
            <div className="flex items-center gap-2 mb-2">
              <LayoutGrid className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-semibold text-emerald-900">Details card</span>
              <span className="text-[10px] text-emerald-700">(left card, under the Description)</span>
            </div>
            <div className="space-y-1.5">
              {detailsFields.length === 0 && (
                <p className="text-xs text-slate-400 italic">No fields here — use a field’s placement dropdown to add one.</p>
              )}
              {detailsFields.map(renderFieldRow)}
            </div>
          </div>

          {/* Information box group */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="ovl-group-infobox">
            <div className="flex items-center gap-2 mb-2">
              <LayoutGrid className="w-4 h-4 text-blue-700" />
              <span className="text-sm font-semibold text-blue-900">Information box</span>
              <span className="text-[10px] text-blue-700">(right card, next to Company & Specialties)</span>
            </div>
            <div className="space-y-1.5">
              {infoBoxFields.length === 0 && (
                <p className="text-xs text-slate-400 italic">No fields here — use a field’s placement dropdown to add one.</p>
              )}
              {infoBoxFields.map(renderFieldRow)}
            </div>
          </div>

          {/* Sections */}
          {sections.map((s, si) => (
            <div key={s.id} className="rounded-lg border border-slate-200 p-3" data-testid={`ovl-group-section-${s.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={s.title}
                  onChange={(e) => renameSection(s.id, e.target.value)}
                  className="h-7 text-sm font-semibold max-w-[260px]"
                  data-testid={`ovl-section-title-${s.id}`}
                />
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={si === 0} onClick={() => moveSection(s.id, 'up')}>
                  <ChevronUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={si === sections.length - 1} onClick={() => moveSection(s.id, 'down')}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
                {sections.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => deleteSection(s.id)} data-testid={`ovl-section-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                {fields.filter((f) => f.placement !== 'info_box' && groupOf(f) === s.id).map(renderFieldRow)}
                {fields.filter((f) => f.placement !== 'info_box' && groupOf(f) === s.id).length === 0 && (
                  <p className="text-xs text-slate-400 italic">Empty section — it won’t appear on the Overview tab.</p>
                )}
              </div>
            </div>
          ))}

          <Button type="button" variant="outline" size="sm" onClick={addSection} data-testid="ovl-add-section-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Section
          </Button>
        </div>

        {/* ---- Live preview ---- */}
        <div className="xl:sticky xl:top-4 self-start">
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> Live preview — Overview tab
            </p>
            <OverviewPreview layoutDraft={layoutDraft} fieldDefs={fieldDefs} />
          </div>
        </div>
      </div>

      {/* ---- Full-size preview modal ---- */}
      <Dialog open={showFullPreview} onOpenChange={setShowFullPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="ovl-full-preview-modal">
          <DialogHeader>
            <DialogTitle>Overview Tab Preview — {requestType}</DialogTitle>
            <DialogDescription>
              How the Overview tab will look with sample values for every custom field. Unsaved changes are included.
            </DialogDescription>
          </DialogHeader>
          <OverviewPreview layoutDraft={layoutDraft} fieldDefs={fieldDefs} full />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OverviewLayoutEditor;
